import { ModelDataType } from 'src/app/interfaces/model-data-type';
import { Owner } from 'src/app/interfaces/models/owner';

import { BaseModel } from './base-model';
import { ClientRegisteredVendor } from './client-registered-vendor';
import { Vendor } from './vendor';

export class LexNexReport extends BaseModel {
  static override columnMap = new Map<
    string,
    { property: string; type: 'column'; dataType: ModelDataType }
  >();
  static override associationMap = new Map<
    string,
    { property: string; type: 'association'; model: string }
  >();
  static override aliasedColumnMap = new Map<
    string,
    { property: string; type: 'alias' }
  >();

  static LexNexReportProperties: (
    | {
        property: keyof LexNexReport;
        type: 'column';
        dataType: ModelDataType;
      }
    | {
        property: keyof LexNexReport;
        type: 'alias';
      }
    | {
        property: keyof LexNexReport;
        type: 'association';
        model: string;
      }
  )[] = [
    { property: 'id', type: 'column', dataType: 'number' },
    { property: 'VendorId', type: 'column', dataType: 'number' },
    {
      property: 'ClientRegisteredVendorId',
      type: 'column',
      dataType: 'number'
    },
    { property: 'OwnerId', type: 'column', dataType: 'number' },
    { property: 'queryId', type: 'column', dataType: 'string' },
    { property: 'reqType', type: 'column', dataType: 'string' },
    { property: 'reqJson', type: 'column', dataType: 'JSON' },
    { property: 'resJson', type: 'column', dataType: 'JSON' },
    { property: 'createdAt', type: 'column', dataType: 'date' },
    { property: 'updatedAt', type: 'column', dataType: 'date' },

    { property: 'Vendor', model: 'Vendor', type: 'association' },
    {
      property: 'ClientRegisteredVendor',
      model: 'ClientRegisteredVendor',
      type: 'association'
    },
    { property: 'Owner', model: 'Owner', type: 'association' }
  ];

  static {
    LexNexReport.LexNexReportProperties.forEach((propertyObject) => {
      const { property, type } = propertyObject;
      if (type === 'column') {
        LexNexReport.columnMap.set(property, propertyObject);
      }
      if (type === 'alias') {
        LexNexReport.aliasedColumnMap.set(
          property,
          propertyObject as { property: string; type: 'alias' }
        );
      }
      if (type === 'association') {
        LexNexReport.associationMap.set(
          property,
          propertyObject as {
            property: string;
            type: 'association';
            model: string;
          }
        );
      }
    });
  }

  constructor(LexNexReportObject: Partial<LexNexReport>) {
    super();

    LexNexReport.LexNexReportProperties.forEach((propertyObject) => {
      const { property } = propertyObject;
      const value = LexNexReportObject[property];
      if (value !== undefined) {
        Object.assign(this, { [property]: value });
      }
    });
  }

  removeHistoricalDataFromCorporateFilingReport() {
    if (this.reqType === 'CorporateSearch2Request') {
      if (!this.resJson) {
        throw new Error(
          'Cannot remove historical data. Missing response JSON.'
        );
      }

      const { CorporateSearch2ResponseEx } = this.resJson as any;
      const { response } = CorporateSearch2ResponseEx;
      const { Records: { Record } = { Record: [] } } = response;

      Record.forEach((r: any) => {
        const {
          CorporateHistories: { CorporateHistory } = { CorporateHistory: [] }
        } = r;
        Object.assign(r.CorporateHistories, {
          CorporateHistory: CorporateHistory.filter(
            (ch: any) =>
              ch.RecordType && ch.RecordType.toUpperCase() !== 'HISTORICAL'
          )
        });
      });

      return Object.assign(this, {
        resJson: JSON.stringify({ CorporateSearch2ResponseEx })
      });
    }

    throw new Error(
      'There was an error removing historical data from corporate search report.'
    );
  }

  id?: number;
  VendorId?: number | null;
  ClientRegisteredVendorId?: number | null;
  OwnerId?: number | null;
  queryId?: string;
  reqType?: string;
  reqJson?: JSON;
  resJson?: JSON;
  createdAt?: Date;
  updatedAt?: Date;

  Vendor?: Vendor;
  ClientRegisteredVendor?: ClientRegisteredVendor;
  Owner?: Owner;
}
